import { Component } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { LaborReviewEstimate, ModalConfig } from '../models/modal-system.interfaces';

export interface ModalCellRendererParams extends ICellRendererParams {
  modalConfig: ModalConfig;
  dbField: string;
  headerName: string;
  onModalClick: (ctx: {
    lading_uuid: string;
    rowData: Record<string, any>;
    modalConfig: ModalConfig;
    headerName: string;
  }) => void;
}

@Component({
  selector: 'app-modal-cell-renderer',
  template: `
    <button
      class="mcr"
      type="button"
      [ngClass]="variantClass"
      [title]="tooltipText"
      (click)="onClick($event)"
    >
      <span class="mcr__rail"></span>

      <span class="mcr__content">
        <span class="mcr__headline">{{ primaryText }}</span>
        <span class="mcr__meta" *ngIf="secondaryText">{{ secondaryText }}</span>
      </span>

      <span class="mcr__icon" *ngIf="iconClass" [attr.aria-label]="iconLabel" [title]="iconLabel">
        <i [class]="iconClass"></i>
      </span>
    </button>
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
      height: 100%;
    }

    .mcr {
      --mcr-rgb: 59, 130, 246;
      display: grid;
      grid-template-columns: auto minmax(0, 1fr) auto;
      align-items: center;
      gap: 0.55rem;
      width: 100%;
      min-height: 32px;
      padding: 0.28rem 0.55rem;
      border: 1px solid rgba(148, 163, 184, 0.16);
      border-radius: 12px;
      background:
        linear-gradient(180deg, rgba(var(--mcr-rgb), 0.1), rgba(var(--mcr-rgb), 0.03)),
        var(--cwo-surface-2, #111827);
      color: var(--cwo-text-main, #f8fafc);
      text-align: left;
      cursor: pointer;
      transition: border-color 0.16s ease, background 0.16s ease, transform 0.16s ease;
    }

    .mcr:hover {
      transform: translateY(-1px);
      border-color: rgba(var(--mcr-rgb), 0.34);
      background:
        linear-gradient(180deg, rgba(var(--mcr-rgb), 0.14), rgba(var(--mcr-rgb), 0.05)),
        var(--cwo-surface, #0f172a);
    }

    .mcr__rail {
      width: 0.32rem;
      height: 1.7rem;
      border-radius: 999px;
      background: linear-gradient(180deg, rgb(var(--mcr-rgb)), rgba(var(--mcr-rgb), 0.28));
      flex: 0 0 auto;
    }

    .mcr__content {
      display: flex;
      flex-direction: column;
      gap: 0.08rem;
      min-width: 0;
    }

    .mcr__headline {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: 0.76rem;
      font-weight: 700;
      color: var(--cwo-text-main, #f8fafc);
    }

    .mcr__meta {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      color: var(--cwo-text-secondary, #94a3b8);
      font-size: 0.66rem;
      font-weight: 500;
    }

    .mcr__icon {
      display: inline-flex;
      justify-content: center;
      align-items: center;
      width: 1.7rem;
      height: 1.7rem;
      border-radius: 999px;
      background: rgba(var(--mcr-rgb), 0.12);
      color: rgb(var(--mcr-rgb));
      flex: 0 0 auto;
    }

    .mcr__icon i {
      font-size: 0.74rem;
    }

    .mcr--status-timeline {
      --mcr-rgb: 14, 165, 233;
    }

    .mcr--technician {
      --mcr-rgb: 139, 92, 246;
    }

    .mcr--labor-review {
      --mcr-rgb: 59, 130, 246;
    }

    .mcr--labor-review-default {
      --mcr-rgb: 14, 165, 233;
    }

    .mcr--labor-review-requested {
      --mcr-rgb: 245, 158, 11;
    }

    .mcr--labor-review-approved {
      --mcr-rgb: 16, 185, 129;
    }

    .mcr--labor-review-in-progress {
      --mcr-rgb: 245, 158, 11;
    }

    .mcr--labor-review-complete {
      --mcr-rgb: 16, 185, 129;
    }

    .mcr--labor-review-over {
      --mcr-rgb: 239, 68, 68;
    }

    .mcr--labor-review-empty {
      --mcr-rgb: 100, 116, 139;
    }

    .mcr--notes {
      --mcr-rgb: 99, 102, 241;
    }

    .mcr--appointment-window {
      --mcr-rgb: 16, 185, 129;
    }

    .mcr--appointment-window-empty {
      --mcr-rgb: 100, 116, 139;
    }
  `],
})
export class ModalCellRendererComponent implements ICellRendererAngularComp {
  primaryText = '';
  secondaryText = '';
  iconClass = '';
  iconLabel = '';
  tooltipText = '';
  variantClass = 'mcr--generic';
  private params!: ModalCellRendererParams;

  agInit(params: ModalCellRendererParams): void {
    this.params = params;
    this.computeDisplay();
  }

  refresh(params: ModalCellRendererParams): boolean {
    this.params = params;
    this.computeDisplay();
    return true;
  }

  onClick(event: MouseEvent): void {
    event.stopPropagation();
    if (!this.params.onModalClick) return;

    const lading_uuid = this.params.data?.['lading_uuid'] ?? '';
    if (!lading_uuid) {
      console.warn('[ModalCellRenderer] No lading_uuid on row data.');
      return;
    }

    this.params.onModalClick({
      lading_uuid,
      rowData: this.params.data,
      modalConfig: this.params.modalConfig,
      headerName: this.params.headerName,
    });
  }

  private computeDisplay(): void {
    const fieldValue = this.params.data?.[this.params.dbField];
    const raw = fieldValue === null || fieldValue === undefined || fieldValue === ''
      ? this.params.value
      : fieldValue;
    const modalType = this.params.modalConfig?.modal_type;

    this.primaryText = 'Open details';
    this.secondaryText = '';
    this.iconClass = this.iconClassForModalType(modalType);
    this.iconLabel = this.params.headerName || 'Open modal';
    this.tooltipText = this.params.headerName;
    this.variantClass = `mcr--${modalType ?? 'generic'}`;

    if (modalType === 'labor_review') {
      this.computeLaborReviewDisplay(raw);
      return;
    }

    if (modalType === 'technician') {
      this.primaryText = raw ? String(raw) : 'Assign technician';
      this.secondaryText = raw ? 'Open technician assignment' : 'No technician assigned';
      this.tooltipText = [this.primaryText, this.secondaryText].filter(Boolean).join(' • ');
      return;
    }

    if (modalType === 'status_timeline') {
      this.primaryText = raw ? String(raw) : 'No status yet';
      this.secondaryText = raw ? 'Open timeline and notes' : 'Open status timeline';
      this.tooltipText = [this.primaryText, this.secondaryText].filter(Boolean).join(' • ');
      return;
    }

    if (modalType === 'appointment_window') {
      this.computeAppointmentWindowDisplay(raw);
      return;
    }

    if (modalType === 'notes') {
      const noteText = this.latestCarrierNoteText(raw);
      const noteCount = this.carrierNoteCount(raw);
      this.primaryText = noteText ? this.truncateText(noteText, 76) : 'No notes';
      this.secondaryText = noteCount > 0
        ? `${noteCount} ${noteCount === 1 ? 'note' : 'notes'} - Open notes`
        : noteText ? 'Latest note - Open notes' : 'Open notes';
      this.tooltipText = noteText || this.secondaryText;
      return;
    }

    if (raw === null || raw === undefined || raw === '') {
      this.primaryText = 'Open details';
      this.secondaryText = 'No value present in row data';
    } else {
      this.primaryText = String(raw);
      this.secondaryText = 'Open modal';
    }

    this.tooltipText = [this.primaryText, this.secondaryText].filter(Boolean).join(' • ');
  }

  private computeAppointmentWindowDisplay(raw: unknown): void {
    const appointmentWindow = this.parseAppointmentWindow(raw);
    const start = this.normalizeWindowDateTime(appointmentWindow.start);
    const end = this.normalizeWindowDateTime(appointmentWindow.end);

    if (!start && !end) {
      this.variantClass = 'mcr--appointment-window mcr--appointment-window-empty';
      this.primaryText = 'Schedule window';
      this.secondaryText = 'No delivery window set';
      this.tooltipText = 'No delivery window set. Open to schedule.';
      return;
    }

    this.variantClass = 'mcr--appointment-window';

    if (start && end && this.windowDatePart(start) === this.windowDatePart(end)) {
      this.primaryText = this.formatWindowDate(this.windowDatePart(start));
      this.secondaryText = [
        this.formatWindowTime(this.windowTimePart(start)),
        this.formatWindowTime(this.windowTimePart(end)),
      ].filter(Boolean).join(' - ') || 'Time not set';
      this.tooltipText = [this.primaryText, this.secondaryText].filter(Boolean).join(' ');
      return;
    }

    const startLabel = this.formatWindowDateTime(start) || 'Start not set';
    const endLabel = this.formatWindowDateTime(end);

    this.primaryText = startLabel;
    this.secondaryText = endLabel ? `to ${endLabel}` : 'End not set';
    this.tooltipText = [this.primaryText, this.secondaryText].filter(Boolean).join(' ');
  }

  private computeLaborReviewDisplay(raw: unknown): void {
    const review = this.parseReview(raw);

    if (!review) {
      this.variantClass = 'mcr--labor-review mcr--labor-review-empty';
      this.primaryText = 'Request review';
      this.secondaryText = raw ? 'Saved review could not be parsed' : 'No labor review submitted';
      this.tooltipText = [this.primaryText, this.secondaryText].filter(Boolean).join(' • ');
      return;
    }

    const actualMinutes = this.actualMinutes(review);
    const allowedMinutes = this.allowedMinutes(review);

    if (review.actual_started_at && !review.actual_completed_at) {
      this.variantClass = 'mcr--labor-review mcr--labor-review-in-progress';
      this.primaryText = 'In progress';
      this.secondaryText = this.compactSummary([
        actualMinutes != null ? `Elapsed ${this.formatMinutes(actualMinutes)}` : '',
        allowedMinutes != null ? `Allowed ${this.formatMinutes(allowedMinutes)}` : '',
      ]) || 'Onsite labor is clocked in';
      this.tooltipText = [this.primaryText, this.secondaryText].filter(Boolean).join(' • ');
      return;
    }

    if (review.actual_started_at && review.actual_completed_at) {
      const overMinutes = actualMinutes == null || allowedMinutes == null
        ? null
        : Math.max(0, actualMinutes - allowedMinutes);
      const wentOver = overMinutes != null && overMinutes > 0;

      this.variantClass = wentOver
        ? 'mcr--labor-review mcr--labor-review-over'
        : 'mcr--labor-review mcr--labor-review-complete';
      this.primaryText = actualMinutes == null ? 'Completed' : `${this.formatMinutes(actualMinutes)} actual`;
      this.secondaryText = wentOver
        ? `Over by ${this.formatMinutes(overMinutes)}`
        : allowedMinutes == null ? 'Completed • No allowance available' : `Within ${this.formatMinutes(allowedMinutes)} allowance`;
      this.tooltipText = [this.primaryText, this.secondaryText].filter(Boolean).join(' • ');
      return;
    }

    const totalMinutes = this.pickMetric(review, 'total');
    const installMinutes = this.pickMetric(review, 'install');
    const handlingMinutes = this.pickMetric(review, 'handling');
    const people = review.approved_people ?? review.requested_people ?? review.estimated_people ?? null;
    const driveHours = this.nullableNumber(review.requested_technician_drive_hours);
    const hotelNights = this.nullableNumber(review.requested_technician_hotel_nights);
    const status = this.formatStatus(review.status);

    this.variantClass = `mcr--labor-review ${this.laborReviewStateClass(review)}`;
    this.primaryText = totalMinutes == null ? 'Estimate pending' : this.formatMinutes(totalMinutes);
    this.secondaryText = this.compactSummary([
      installMinutes != null ? `Install ${this.formatMinutes(installMinutes)}` : '',
      handlingMinutes != null ? `Handling ${this.formatMinutes(handlingMinutes)}` : '',
      people != null ? `Crew ${this.formatWholeNumber(people)}` : '',
      driveHours != null && driveHours > 0 ? `Drive ${driveHours}h` : '',
      hotelNights != null && hotelNights > 0 ? `Hotel ${Math.round(hotelNights)}n` : '',
      status,
    ]) || 'Open labor review details';
    this.tooltipText = [this.primaryText, this.secondaryText].filter(Boolean).join(' • ');
  }

  private iconClassForModalType(modalType: ModalConfig['modal_type'] | undefined): string {
    switch (modalType) {
      case 'status_timeline':
        return 'fa-solid fa-clock-rotate-left';
      case 'technician':
        return 'fa-solid fa-user-gear';
      case 'labor_review':
        return 'fa-solid fa-stopwatch';
      case 'notes':
        return 'fa-solid fa-note-sticky';
      case 'appointment_window':
        return 'fa-solid fa-calendar-check';
      default:
        return 'fa-solid fa-up-right-from-square';
    }
  }

  private parseAppointmentWindow(raw: unknown): { start: unknown; end: unknown } {
    if (!raw) return { start: null, end: null };

    if (typeof raw === 'object') {
      return {
        start: (raw as any).start ?? null,
        end: (raw as any).end ?? null,
      };
    }

    if (typeof raw !== 'string') return { start: null, end: null };

    const parsed = this.parseMaybeJson(raw);
    if (!parsed || typeof parsed !== 'object') return { start: null, end: null };

    return {
      start: (parsed as any).start ?? null,
      end: (parsed as any).end ?? null,
    };
  }

  private normalizeWindowDateTime(value: unknown): string {
    if (value === null || value === undefined || value === '') return '';

    const text = String(value).trim();
    const date = this.windowDatePart(text);
    const time = this.windowTimePart(text);
    return date && time ? `${date} ${time}` : text;
  }

  private formatWindowDateTime(value: string): string {
    if (!value) return '';

    const date = this.formatWindowDate(this.windowDatePart(value));
    const time = this.formatWindowTime(this.windowTimePart(value));
    return [date, time].filter(Boolean).join(' ');
  }

  private windowDatePart(value: unknown): string {
    const match = String(value || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    return match ? `${match[1]}-${match[2]}-${match[3]}` : '';
  }

  private windowTimePart(value: unknown): string {
    const match = String(value || '').trim().match(/(?:^|\s|T)(\d{2}):(\d{2})/);
    return match ? `${match[1]}:${match[2]}` : '';
  }

  private formatWindowDate(value: string): string {
    const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return '';

    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  private formatWindowTime(value: string): string {
    const match = String(value || '').match(/^(\d{2}):(\d{2})$/);
    if (!match) return '';

    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return '';

    const suffix = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${String(minutes).padStart(2, '0')} ${suffix}`;
  }

  private latestCarrierNoteText(raw: unknown): string {
    if (raw === null || raw === undefined || raw === '') {
      const directValue = this.firstStringValue([
        '__latest_note',
        'latest_carrier_note',
        'carrier_lading_note',
        'carrier_note',
        'latest_lading_note',
        'v_lading_notes.note',
        'lading_note',
        'notes',
        'note',
      ]);
      if (directValue) return directValue;
    }

    if (typeof raw === 'string' && raw.trim()) {
      const parsed = this.parseMaybeJson(raw);
      if (Array.isArray(parsed)) {
        const note = this.latestCarrierNoteFromArray(parsed);
        return note ? String(note.note || '').trim() : raw.trim();
      }
      if (parsed && typeof parsed === 'object') {
        return String((parsed as any).note || raw).trim();
      }
      return raw.trim();
    }

    if (Array.isArray(raw)) {
      const note = this.latestCarrierNoteFromArray(raw);
      return note ? String(note.note || '').trim() : '';
    }

    if (raw && typeof raw === 'object') {
      return String((raw as any).note || '').trim();
    }

    const note = this.latestCarrierNoteFromArray(this.rowNotesArray());
    return note ? String(note.note || '').trim() : '';
  }

  private carrierNoteCount(raw: unknown): number {
    const directCount = this.firstNumberValue([
      'carrier_lading_note_count',
      'carrier_note_count',
      'lading_note_count',
      'notes_count',
    ]);
    if (directCount !== null) return directCount;

    if (Array.isArray(raw)) return this.carrierNotesFromArray(raw).length;
    if (typeof raw === 'string' && raw.trim()) {
      const parsed = this.parseMaybeJson(raw);
      if (Array.isArray(parsed)) return this.carrierNotesFromArray(parsed).length;
      return 1;
    }
    if (raw && typeof raw === 'object') return 1;

    return this.carrierNotesFromArray(this.rowNotesArray()).length;
  }

  private latestCarrierNoteFromArray(notes: any[]): any | null {
    const visibleNotes = this.carrierNotesFromArray(notes);
    if (!visibleNotes.length) return null;
    return visibleNotes.sort((a, b) => this.noteSortValue(b) - this.noteSortValue(a))[0];
  }

  private carrierNotesFromArray(notes: any[]): any[] {
    const carrierContactUuid = String(this.params.data?.['carrier_contact_uuid'] || '').trim();
    return notes.filter((note) => {
      if (!note || typeof note !== 'object') return false;
      if (!String(note.note || '').trim()) return false;
      if (!carrierContactUuid) return true;
      return String(note.contact_uuid || '').trim() === carrierContactUuid;
    });
  }

  private rowNotesArray(): any[] {
    const rowNotes = this.params.data?.['lading_notes'] || this.params.data?.['carrier_lading_notes'] || [];
    return Array.isArray(rowNotes) ? rowNotes : [];
  }

  private noteSortValue(note: any): number {
    const value = note?.date_last_modified || note?.date || note?.created || '';
    const parsed = value ? new Date(value).getTime() : 0;
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  private firstStringValue(fields: string[]): string {
    for (const field of fields) {
      const value = String(this.params.data?.[field] || '').trim();
      if (value) return value;
    }
    return '';
  }

  private firstNumberValue(fields: string[]): number | null {
    for (const field of fields) {
      const value = this.params.data?.[field];
      if (value === null || value === undefined || value === '') continue;
      const numericValue = Number(value);
      if (Number.isFinite(numericValue)) return numericValue;
    }
    return null;
  }

  private parseMaybeJson(value: string): unknown {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }

  private truncateText(value: string, maxLength: number): string {
    const text = String(value || '').trim();
    if (text.length <= maxLength) return text;
    return `${text.slice(0, Math.max(0, maxLength - 3)).trimEnd()}...`;
  }

  private parseReview(raw: unknown): LaborReviewEstimate | null {
    if (!raw) return null;
    if (typeof raw === 'object') return raw as LaborReviewEstimate;
    if (typeof raw !== 'string') return null;

    try {
      return JSON.parse(raw) as LaborReviewEstimate;
    } catch {
      return null;
    }
  }

  private laborReviewStateClass(review: LaborReviewEstimate): string {
    const status = String(review.status ?? '').toLowerCase();

    if (
      status === 'approved' ||
      review.approved_total_minutes != null ||
      review.approved_install_minutes != null ||
      review.approved_handling_minutes != null ||
      review.approved_people != null ||
      !!review.approved_at
    ) {
      return 'mcr--labor-review-approved';
    }

    if (
      status === 'requested' ||
      status === 'pending' ||
      review.requested_total_minutes != null ||
      review.requested_install_minutes != null ||
      review.requested_handling_minutes != null ||
      review.requested_people != null ||
      review.requested_technician_drive_hours != null ||
      Number(review.requested_technician_hotel_nights || 0) > 0 ||
      !!review.requested_notes ||
      !!review.requested_at
    ) {
      return 'mcr--labor-review-requested';
    }

    if (
      review.estimated_total_minutes != null ||
      review.estimated_install_minutes != null ||
      review.estimated_handling_minutes != null ||
      review.estimated_people != null
    ) {
      return 'mcr--labor-review-default';
    }

    return 'mcr--labor-review-empty';
  }

  private pickMetric(review: LaborReviewEstimate, type: 'install' | 'handling' | 'total'): number | null {
    if (type === 'install') {
      return review.approved_install_minutes
        ?? review.requested_install_minutes
        ?? review.estimated_install_minutes
        ?? null;
    }

    if (type === 'handling') {
      return review.approved_handling_minutes
        ?? review.requested_handling_minutes
        ?? review.estimated_handling_minutes
        ?? null;
    }

    return review.approved_total_minutes
      ?? review.requested_total_minutes
      ?? review.estimated_total_minutes
      ?? null;
  }

  private actualMinutes(review: LaborReviewEstimate): number | null {
    const startedMs = this.timestampMillis(review.actual_started_at);
    if (startedMs === null) return null;

    const completedMs = this.timestampMillis(review.actual_completed_at) ?? Date.now();
    return Math.max(0, completedMs - startedMs) / 60000;
  }

  private allowedMinutes(review: LaborReviewEstimate): number | null {
    const approved = this.nullableNumber(review.approved_total_minutes);
    if (approved !== null) return approved;
    return this.nullableNumber(review.estimated_total_minutes);
  }

  private nullableNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
  }

  private timestampMillis(value: string | null | undefined): number | null {
    if (!value) return null;
    const parsed = new Date(value).getTime();
    return Number.isFinite(parsed) ? parsed : null;
  }

  private formatMinutes(value: number | null): string {
    if (value == null || Number.isNaN(Number(value))) return '—';
    const rounded = Math.round(Number(value));
    const hours = Math.floor(rounded / 60);
    const minutes = rounded % 60;

    if (hours <= 0) return `${minutes}m`;
    if (minutes === 0) return `${hours}h`;
    return `${hours}h ${minutes}m`;
  }

  private formatWholeNumber(value: number | null): string {
    if (value == null || Number.isNaN(Number(value))) return '—';
    return String(Math.max(0, Math.ceil(Number(value))));
  }

  private formatStatus(value: string | null | undefined): string {
    if (!value) return 'Pending';
    return value
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (match) => match.toUpperCase());
  }

  private compactSummary(parts: string[]): string {
    return parts.filter(Boolean).join(' • ');
  }
}
